import type { AuthAppearance } from '../../utils/authAppearance';
import React from 'react';
import { useThemeMode } from '../../design-system/theme';
import { Helmet } from 'react-helmet-async';
import SiteRouteHelmet from '../../components/SiteRouteHelmet';
import { ShieldCheck } from 'lucide-react';
import LoginRedirect from '../../components/LoginRedirect';
import botspotLogo from '../../assets/botspot_logo_horizontal.png';
import './AuthFirstSalesPage.css';
import './ClarityAuth.css';
import TacticalLedger from './authFirstDesigns/TacticalLedger';
import type { TacticalLedgerContent } from './authFirstDesigns/types';

type AuthFirstSalesPageProps = {
  appearance?: AuthAppearance;
  analyticsContext?: Record<string, unknown>;
  authDescriptionOverride?: string;
  authHeadingIdOverride?: string;
  authSourceOverride?: string;
  entryMode?: 'discovery' | 'checkout' | 'login' | 'signup';
  forceFreshLogin?: boolean;
  hideAuthHeaderFromAssistiveTech?: boolean;
  canonicalHrefOverride?: string;
  desktopProductSubheadingOverride?: string;
  documentTitleOverride?: string;
  metaDescriptionOverride?: string;
  mobileProductSubheadingOverride?: string;
  newSignupReturnTo?: string;
  onAuthenticated?: () => void;
  presentation?: 'page' | 'overlay';
  productContent?: TacticalLedgerContent;
  returnTo: string;
};

const desktopProductSubheading =
  'Build with AI, test against historical data, then run with your broker when you are ready.';

const mobileProductSubheading =
  'Build custom bots, test them on historical data, and run them with your broker.';

const getMobileLayoutPreference = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  Boolean(window.matchMedia('(max-width: 960px)')?.matches);

const AuthFirstSalesPage = ({
  appearance = 'control',
  analyticsContext,
  authDescriptionOverride,
  authHeadingIdOverride,
  authSourceOverride,
  canonicalHrefOverride,
  desktopProductSubheadingOverride,
  documentTitleOverride,
  entryMode = 'discovery',
  forceFreshLogin = false,
  hideAuthHeaderFromAssistiveTech = false,
  metaDescriptionOverride,
  mobileProductSubheadingOverride,
  newSignupReturnTo,
  onAuthenticated,
  presentation = 'page',
  productContent,
  returnTo,
}: AuthFirstSalesPageProps): React.ReactElement => {
  const [websiteTheme] = useThemeMode();
  const isMobileLayout = getMobileLayoutPreference();
  const productSubheading = isMobileLayout
    ? mobileProductSubheadingOverride || mobileProductSubheading
    : desktopProductSubheadingOverride || desktopProductSubheading;

  const isClarity = appearance === 'clarity';
  const isCheckoutEntry = entryMode === 'checkout';
  const isLoginEntry = entryMode === 'login';
  const isSignupEntry = entryMode === 'signup';
  // Login and signup should be a normal auth box. Keep the product pitch on
  // sales discovery and checkout account gates only.
  const isAuthOnlyEntry = isLoginEntry || isSignupEntry || isClarity;
  const navAction = isCheckoutEntry ? 'Continue' : isLoginEntry ? 'Sign in' : 'Create account';
  const authHeading = isCheckoutEntry
    ? 'Create or sign in to continue'
    : isLoginEntry
      ? 'Sign in to BotSpot'
      : isClarity
        ? 'Create your account'
        : 'Create your BotSpot account';
  const defaultAuthDescription = isCheckoutEntry
    ? "We'll sign you in, or create an account. Your plan is saved."
    : isLoginEntry
      ? "Sign in with Google, Apple, or email. Create an account if you're new."
      : "We'll create an account, or sign you in if one exists.";
  const authDescription = authDescriptionOverride || defaultAuthDescription;
  const authSource =
    authSourceOverride ||
    (isCheckoutEntry
      ? 'checkout_auth_first'
      : isLoginEntry
        ? 'login_auth_first'
        : isSignupEntry
          ? 'signup_auth_first'
          : 'sales_auth_first');
  const assuranceCopy = isCheckoutEntry
    ? 'Nothing is charged on this page.'
    : isSignupEntry
      ? 'Creating an account does not connect a broker or place a trade.'
      : 'Signing in does not connect a broker or place a trade.';
  const isOverlay = presentation === 'overlay';
  const subheading = <p className="auth-first-sales__subheading">{productSubheading}</p>;

  const authContent = (
    <aside
      aria-label={
        isCheckoutEntry
          ? 'Create or sign in to continue'
          : isLoginEntry
            ? 'Sign in to BotSpot'
            : 'Create your BotSpot account'
      }
      className="auth-first-sales__conversion"
      id="auth-card-section"
    >
      <div className="auth-first-sales__auth-card">
        <LoginRedirect
          analyticsContext={analyticsContext}
          descriptionOverride={authDescription}
          forceFreshLoginOverride={forceFreshLogin}
          headingIdOverride={authHeadingIdOverride}
          headingOverride={authHeading}
          hideDescriptionOverride={isAuthOnlyEntry && !isClarity}
          hideHeaderFromAssistiveTechOverride={hideAuthHeaderFromAssistiveTech}
          newSignupReturnToOverride={newSignupReturnTo}
          onAuthenticated={onAuthenticated}
          productLedOverride={!isAuthOnlyEntry}
          returnToOverride={returnTo}
          sourceOverride={authSource}
        />
      </div>

      {(!isClarity || isCheckoutEntry) && (
        <div className="auth-first-sales__assurance">
          <ShieldCheck aria-hidden="true" focusable="false" />
          <p>{assuranceCopy}</p>
        </div>
      )}

      {isCheckoutEntry ? (
        <div className="auth-first-sales__checkout-context">
          <a className="auth-first-sales__pricing-link" href="/pricing">
            Return to pricing
          </a>
        </div>
      ) : null}
    </aside>
  );

  return (
    <div
      className={`auth-first-sales auth-page bsui-scope${isClarity ? ' auth-first-sales--clarity' : ''}${isOverlay ? ' auth-first-sales--overlay' : ''}${isAuthOnlyEntry ? ' auth-first-sales--auth-only' : ''}`}
      data-bs-theme={websiteTheme}
      data-design={isClarity ? 'clarity' : isAuthOnlyEntry ? 'auth-only' : 'tactical-ledger'}
      data-entry-mode={entryMode}
      data-presentation={presentation}
    >
      {entryMode === 'discovery' && !documentTitleOverride && !canonicalHrefOverride ? (
        <SiteRouteHelmet path="/sales" />
      ) : (
        <Helmet>
          <title>
            {documentTitleOverride ||
              (isCheckoutEntry
                ? 'Create Your BotSpot Account | Continue to Checkout'
                : isLoginEntry
                  ? 'Sign In to BotSpot'
                  : isSignupEntry
                    ? 'Create Your BotSpot Account'
                    : 'BotSpot | Build and Backtest a Trading Bot with AI')}
          </title>
          <meta
            name="description"
            content={
              metaDescriptionOverride ||
              (isCheckoutEntry
                ? 'Create or sign in to BotSpot, then return to checkout with your plan saved.'
                : isAuthOnlyEntry
                  ? 'Sign in to BotSpot with Google or email.'
                  : "Build a trading strategy with BotSpot's AI Agent, backtest it on historical data, and connect a broker when you're ready.")
            }
          />
          {canonicalHrefOverride && <link rel="canonical" href={canonicalHrefOverride} />}
        </Helmet>
      )}
      <Helmet>
        <meta name="theme-color" content="#080b18" />
      </Helmet>

      {!isOverlay && !isAuthOnlyEntry && (
        <header className="auth-first-nav">
          <div className="auth-first-nav__inner">
            <a aria-label="BotSpot home" className="auth-first-nav__logo" href="/">
              <img alt="BotSpot by LumiWealth" height={429} src={botspotLogo} width={1385} />
            </a>
            <a className="auth-first-nav__action" href="#auth-card-section">
              {navAction}
            </a>
          </div>
        </header>
      )}

      <main
        aria-labelledby={isAuthOnlyEntry ? undefined : 'auth-first-title'}
        aria-label={isAuthOnlyEntry ? authHeading : undefined}
        className={`auth-first-sales__container${isAuthOnlyEntry ? ' auth-first-sales__container--auth-only' : ''}`}
      >
        {isAuthOnlyEntry ? (
          <div className="auth-first-auth-only">
            {isClarity && (
              <a aria-label="BotSpot home" className="clarity-auth__brand" href="/">
                BotSpot<span>by LumiWealth</span>
              </a>
            )}
            {authContent}
          </div>
        ) : (
          <TacticalLedger
            authContent={authContent}
            content={productContent}
            subheading={subheading}
            titleId="auth-first-title"
          />
        )}
      </main>

      {!isOverlay && !isAuthOnlyEntry && (
        <footer className="auth-first-sales__footer">
          <p>Trading involves risk. Backtests and paper trading do not guarantee future results.</p>
        </footer>
      )}
    </div>
  );
};

export default AuthFirstSalesPage;
